import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useReducer,
  useRef
} from "react";
import {
  ArrowRight,
  CheckCircle2,
  CircleHelp,
  LoaderCircle,
  Map,
  RefreshCw,
  RotateCcw,
  Save
} from "lucide-react";

import type { LevelDefinition } from "@/domain";
import type {
  ActivityHostCommand,
  ActivityHostCompletionMetrics
} from "@/features/activity-host";
import { PrimaryCommandButton } from "@/ui";

import type {
  PlayAudioCommands,
  PlayNarrationRequest,
  PlayOrchestratorProps,
  PlayRendererRegistration,
  PlaySessionCompletion,
  PlaySessionSaveResult,
  PlaySessionSaver
} from "./contracts";
import {
  resolvePlayLevel,
  resolvePlayRenderer
} from "./resolver";
import "./PlayOrchestrator.css";

type OperationalPhase =
  | "loading"
  | "instruction"
  | "active"
  | "level-error";

type NavigationReadySessionState =
  | {
      readonly attemptNumber: number;
      readonly completion: ActivityHostCompletionMetrics;
      readonly phase: "saved";
      readonly saveResult: Extract<
        PlaySessionSaveResult,
        { readonly navigation: "durable" }
      >;
    }
  | {
      readonly attemptNumber: number;
      readonly completion: ActivityHostCompletionMetrics;
      readonly phase: "temporary";
      readonly saveResult: Extract<
        PlaySessionSaveResult,
        { readonly navigation: "temporary" }
      >;
    };

type PlaySessionState =
  | {
      readonly attemptNumber: number;
      readonly phase: OperationalPhase;
    }
  | {
      readonly attemptNumber: number;
      readonly completion: ActivityHostCompletionMetrics;
      readonly phase: "result" | "saving" | "save-error";
    }
  | NavigationReadySessionState;

type PlaySessionEvent =
  | { readonly type: "renderer-ready" }
  | { readonly type: "activity-started" }
  | { readonly type: "activity-loading" }
  | { readonly type: "activity-error" }
  | {
      readonly type: "completion";
      readonly completion: ActivityHostCompletionMetrics;
    }
  | { readonly type: "save-started" }
  | {
      readonly type: "save-failed";
      readonly attemptNumber: number;
    }
  | {
      readonly type: "save-succeeded";
      readonly attemptNumber: number;
      readonly result: PlaySessionSaveResult;
    }
  | { readonly type: "replay" };

type PlayRouteErrorCode =
  | "invalid-level-id"
  | "missing-level"
  | "duplicate-level"
  | "invalid-level"
  | "missing-renderer"
  | "duplicate-renderer"
  | "missing-profile";

interface ResolvedPlaySessionProps {
  readonly audioCommands: PlayAudioCommands;
  readonly level: LevelDefinition;
  readonly narrationOwnership: NarrationOwnership;
  readonly narrationCaption: PlayOrchestratorProps["narrationCaption"];
  readonly nowMs: PlayOrchestratorProps["nowMs"];
  readonly onAttemptExit: PlayOrchestratorProps["onAttemptExit"];
  readonly onAttemptResult: PlayOrchestratorProps["onAttemptResult"];
  readonly onAttemptStart: PlayOrchestratorProps["onAttemptStart"];
  readonly onPlayLevel: PlayOrchestratorProps["onPlayLevel"];
  readonly onReturnToMap: PlayOrchestratorProps["onReturnToMap"];
  readonly profileId: string;
  readonly reducedMotion: PlayOrchestratorProps["reducedMotion"];
  readonly renderer: PlayRendererRegistration;
  readonly sessionSaver: PlaySessionSaver;
}

interface PlayRouteErrorProps {
  readonly code: PlayRouteErrorCode;
  readonly onReturnToMap: () => void;
}

interface NarrationOwnership {
  claim(): number;
  releaseIfOwned(token: number): boolean;
}

type AttemptLifecyclePhase = "pending" | "active" | "result" | "exit";

interface AttemptLifecycleRecord {
  readonly event: {
    readonly attemptNumber: number;
    readonly level: LevelDefinition;
    readonly profileId: string;
  };
  phase: AttemptLifecyclePhase;
}

function createInitialState(attemptNumber = 1): PlaySessionState {
  return { attemptNumber, phase: "loading" };
}

function createNarrationOwnership(): NarrationOwnership {
  let generation = 0;

  return {
    claim() {
      generation += 1;
      return generation;
    },
    releaseIfOwned(token) {
      if (token !== generation) {
        return false;
      }

      generation += 1;
      return true;
    }
  };
}

function sessionReducer(
  state: PlaySessionState,
  event: PlaySessionEvent
): PlaySessionState {
  switch (event.type) {
    case "renderer-ready":
      return state.phase === "loading"
        ? { ...state, phase: "instruction" }
        : state;
    case "activity-started":
      return state.phase === "instruction" || state.phase === "active"
        ? { ...state, phase: "active" }
        : state;
    case "activity-loading":
      return state.phase === "loading" ||
        state.phase === "instruction" ||
        state.phase === "active" ||
        state.phase === "level-error"
        ? { attemptNumber: state.attemptNumber, phase: "loading" }
        : state;
    case "activity-error":
      return state.phase === "loading" ||
        state.phase === "instruction" ||
        state.phase === "active"
        ? { attemptNumber: state.attemptNumber, phase: "level-error" }
        : state;
    case "completion":
      return state.phase === "active"
        ? {
            attemptNumber: state.attemptNumber,
            completion: event.completion,
            phase: "result"
          }
        : state;
    case "save-started":
      return state.phase === "result" || state.phase === "save-error"
        ? { ...state, phase: "saving" }
        : state;
    case "save-failed":
      return state.phase === "saving" &&
        state.attemptNumber === event.attemptNumber
        ? { ...state, phase: "save-error" }
        : state;
    case "save-succeeded":
      if (
        state.phase !== "saving" ||
        state.attemptNumber !== event.attemptNumber
      ) {
        return state;
      }

      return event.result.navigation === "durable"
        ? {
            attemptNumber: state.attemptNumber,
            completion: state.completion,
            phase: "saved",
            saveResult: event.result
          }
        : {
            attemptNumber: state.attemptNumber,
            completion: state.completion,
            phase: "temporary",
            saveResult: event.result
          };
    case "replay":
      return state.phase === "saved" || state.phase === "temporary"
        ? createInitialState(state.attemptNumber + 1)
        : state;
  }

  const exhaustiveEvent: never = event;
  return exhaustiveEvent;
}

function isNavigationReady(
  state: PlaySessionState
): state is NavigationReadySessionState {
  return state.phase === "saved" || state.phase === "temporary";
}

function runAudioCommand(command: () => void | Promise<unknown>): void {
  try {
    void Promise.resolve(command()).catch(() => undefined);
  } catch {
    // Missing optional audio must not stop the activity route.
  }
}

function getRouteErrorMessage(code: PlayRouteErrorCode): string {
  switch (code) {
    case "invalid-level-id":
    case "missing-level":
      return "这条航线还没找到。先回岛屿地图，点点陪你换一站。";
    case "duplicate-level":
    case "invalid-level":
      return "这一关的小游戏还没摆好。先换一条航线吧。";
    case "missing-renderer":
    case "duplicate-renderer":
      return "这一关还在收拾道具，过一会儿再来看看。";
    case "missing-profile":
      return "先请一位小船员上船，我们再出发。";
  }
}

function PlayRouteError({ code, onReturnToMap }: PlayRouteErrorProps) {
  const rootRef = useRef<HTMLElement>(null);

  useEffect(() => {
    rootRef.current
      ?.querySelector<HTMLButtonElement>("[data-play-control='return-map']")
      ?.focus();
  }, [code]);

  return (
    <section
      className="play-orchestrator play-orchestrator__route-error"
      data-play-phase="level-error"
      ref={rootRef}
    >
      <CircleHelp aria-hidden="true" className="play-orchestrator__state-icon" />
      <h1>这次先停一下</h1>
      <p role="alert">{getRouteErrorMessage(code)}</p>
      <PrimaryCommandButton
        data-play-control="return-map"
        icon={Map}
        onClick={onReturnToMap}
      >
        回到岛屿地图
      </PrimaryCommandButton>
    </section>
  );
}

function ResolvedPlaySession({
  audioCommands,
  level,
  narrationOwnership,
  narrationCaption = null,
  nowMs,
  onAttemptExit,
  onAttemptResult,
  onAttemptStart,
  onPlayLevel,
  onReturnToMap,
  profileId,
  reducedMotion,
  renderer,
  sessionSaver
}: ResolvedPlaySessionProps) {
  const [session, dispatch] = useReducer(
    sessionReducer,
    undefined,
    () => createInitialState()
  );
  const rootRef = useRef<HTMLElement>(null);
  const sessionRef = useRef(session);
  const mountedRef = useRef(true);
  const lifecycleVersionRef = useRef(0);
  const audioCommandsRef = useRef(audioCommands);
  const narrationGenerationRef = useRef<number | null>(null);
  const readyHandledRef = useRef(false);
  const completionHandledAttemptRef = useRef<number | null>(null);
  const saveRequestRef = useRef<PlaySessionCompletion | null>(null);
  const saveInFlightAttemptRef = useRef<number | null>(null);
  const saveOperationRef = useRef(0);
  const attemptLifecycleRef = useRef<AttemptLifecycleRecord | null>(null);
  const attemptLifecycleVersionRef = useRef(0);
  const titleIdPrefix = useId();
  const completionTitleId = titleIdPrefix + "-completion";
  const saveErrorTitleId = titleIdPrefix + "-save-error";
  const readyTitleId = titleIdPrefix + "-ready";

  const narrationRequest = useMemo<PlayNarrationRequest>(
    () => ({
      audioPath: level.promptAudio ?? null,
      caption: level.instruction,
      levelId: level.id
    }),
    [level.id, level.instruction, level.promptAudio]
  );

  useEffect(() => {
    const lifecycleVersion = attemptLifecycleVersionRef.current + 1;
    attemptLifecycleVersionRef.current = lifecycleVersion;
    const record: AttemptLifecycleRecord = {
      event: {
        attemptNumber: session.attemptNumber,
        level,
        profileId
      },
      phase: "pending"
    };
    attemptLifecycleRef.current = record;

    queueMicrotask(() => {
      if (
        attemptLifecycleVersionRef.current === lifecycleVersion &&
        attemptLifecycleRef.current === record &&
        record.phase === "pending"
      ) {
        record.phase = "active";
        onAttemptStart?.(record.event);
      }
    });

    return () => {
      queueMicrotask(() => {
        if (attemptLifecycleRef.current !== record) {
          return;
        }

        if (record.phase === "active") {
          record.phase = "exit";
          onAttemptExit?.(record.event);
        }

        attemptLifecycleRef.current = null;
      });
    };
  }, [
    level,
    onAttemptExit,
    onAttemptStart,
    profileId,
    session.attemptNumber
  ]);

  useEffect(() => {
    audioCommandsRef.current = audioCommands;
  }, [audioCommands]);

  const dispatchSession = useCallback((event: PlaySessionEvent) => {
    // Renderer callbacks can outlive a render, so gate them against the
    // reducer's latest transition instead of a captured phase.
    sessionRef.current = sessionReducer(sessionRef.current, event);
    dispatch(event);
  }, []);

  const claimNarration = useCallback(() => {
    narrationGenerationRef.current = narrationOwnership.claim();
  }, [narrationOwnership]);

  const stopOwnedNarration = useCallback(() => {
    const ownedGeneration = narrationGenerationRef.current;

    if (
      ownedGeneration === null ||
      !narrationOwnership.releaseIfOwned(ownedGeneration)
    ) {
      return;
    }

    narrationGenerationRef.current = null;
    runAudioCommand(() => audioCommandsRef.current.stopNarration());
  }, [narrationOwnership]);

  useEffect(() => {
    const mountedVersion = lifecycleVersionRef.current + 1;
    lifecycleVersionRef.current = mountedVersion;
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      queueMicrotask(() => {
        if (lifecycleVersionRef.current === mountedVersion) {
          saveOperationRef.current += 1;
          stopOwnedNarration();
        }
      });
    };
  }, [stopOwnedNarration]);

  const startSave = useCallback(
    (
      completion: ActivityHostCompletionMetrics,
      attemptNumber: number
    ) => {
      if (saveInFlightAttemptRef.current === attemptNumber) {
        return;
      }

      const existingSaveRequest = saveRequestRef.current;
      const saveRequest =
        existingSaveRequest !== null &&
        existingSaveRequest.attemptNumber === attemptNumber
          ? existingSaveRequest
          : {
              attemptNumber,
              level,
              metrics: completion,
              profileId
            };
      saveRequestRef.current = saveRequest;

      const operation = saveOperationRef.current + 1;
      saveOperationRef.current = operation;
      saveInFlightAttemptRef.current = attemptNumber;
      dispatchSession({ type: "save-started" });

      void Promise.resolve()
        .then(() => sessionSaver.saveCompletion(saveRequest))
        .then(
          (result) => {
            if (
              !mountedRef.current ||
              saveOperationRef.current !== operation
            ) {
              return;
            }

            saveInFlightAttemptRef.current = null;
            dispatchSession({
              type: "save-succeeded",
              attemptNumber,
              result
            });
            runAudioCommand(() => audioCommands.playSuccess(saveRequest));
          },
          () => {
            if (
              !mountedRef.current ||
              saveOperationRef.current !== operation
            ) {
              return;
            }

            saveInFlightAttemptRef.current = null;
            dispatchSession({ type: "save-failed", attemptNumber });
          }
        );
    },
    [
      audioCommands,
      dispatchSession,
      level,
      profileId,
      sessionSaver
    ]
  );

  useEffect(() => {
    if (session.phase === "result") {
      startSave(session.completion, session.attemptNumber);
    }
  }, [session, startSave]);

  useEffect(() => {
    if (!isNavigationReady(session)) {
      return;
    }

    const lifecycle = attemptLifecycleRef.current;
    if (
      lifecycle !== null &&
      lifecycle.event.attemptNumber === session.attemptNumber &&
      lifecycle.phase === "active"
    ) {
      lifecycle.phase = "result";
      onAttemptResult?.(lifecycle.event);
    }
  }, [onAttemptResult, session]);

  useEffect(() => {
    let selector: string | null = null;

    switch (session.phase) {
      case "result":
      case "saving":
        selector = "[data-play-focus='completion']";
        break;
      case "save-error":
        selector = "[data-play-control='retry-save']";
        break;
      case "saved":
      case "temporary":
        selector = "[data-play-focus='ready']";
        break;
      case "loading":
      case "instruction":
      case "active":
      case "level-error":
        break;
    }

    if (selector !== null) {
      rootRef.current?.querySelector<HTMLElement>(selector)?.focus();
    }
  }, [session.phase]);

  const handleReady = useCallback(() => {
    dispatchSession({ type: "renderer-ready" });

    if (readyHandledRef.current) {
      return;
    }

    readyHandledRef.current = true;
    claimNarration();
    runAudioCommand(() => audioCommands.playNarration(narrationRequest));
  }, [
    audioCommands,
    claimNarration,
    dispatchSession,
    narrationRequest
  ]);

  const handleCommand = useCallback(
    (command: ActivityHostCommand) => {
      switch (command.type) {
        case "start":
        case "resume":
          dispatchSession({ type: "activity-started" });
          return;
        case "retry-level":
          readyHandledRef.current = false;
          dispatchSession({ type: "activity-loading" });
          return;
        case "replay-narration":
          runAudioCommand(() =>
            audioCommands.replayNarration(narrationRequest)
          );
          return;
        case "request-hint":
          if (renderer.feedbackAudioPolicy === "generic") {
            runAudioCommand(() => audioCommands.playFeedback("hint"));
          }
          return;
        case "retry":
          if (renderer.feedbackAudioPolicy === "generic") {
            runAudioCommand(() => audioCommands.playFeedback("retry"));
          }
          return;
        case "pause":
        case "replay":
        case "reduced-motion-changed":
          return;
      }

      const exhaustiveCommand: never = command;
      return exhaustiveCommand;
    },
    [audioCommands, dispatchSession, narrationRequest, renderer.feedbackAudioPolicy]
  );

  const handleComplete = useCallback(
    (completion: ActivityHostCompletionMetrics) => {
      if (
        !completion.completed ||
        completionHandledAttemptRef.current === session.attemptNumber
      ) {
        return;
      }

      completionHandledAttemptRef.current = session.attemptNumber;
      dispatchSession({ type: "completion", completion });
    },
    [dispatchSession, session.attemptNumber]
  );

  const handleLevelError = useCallback(() => {
    dispatchSession({ type: "activity-error" });
  }, [dispatchSession]);

  const handleReturnToMap = useCallback(() => {
    const currentSession = sessionRef.current;

    if (
      currentSession.phase === "result" ||
      currentSession.phase === "saving" ||
      currentSession.phase === "save-error"
    ) {
      return;
    }

    const lifecycle = attemptLifecycleRef.current;
    if (lifecycle !== null && lifecycle.phase === "active") {
      lifecycle.phase = "exit";
      onAttemptExit?.(lifecycle.event);
    }

    stopOwnedNarration();
    onReturnToMap();
  }, [onAttemptExit, onReturnToMap, stopOwnedNarration]);

  const handleRetrySave = useCallback(() => {
    const currentSession = sessionRef.current;

    if (currentSession.phase === "save-error") {
      startSave(
        currentSession.completion,
        currentSession.attemptNumber
      );
    }
  }, [startSave]);

  const handleReplay = useCallback(() => {
    const currentSession = sessionRef.current;

    if (!isNavigationReady(currentSession)) {
      return;
    }

    saveOperationRef.current += 1;
    saveInFlightAttemptRef.current = null;
    saveRequestRef.current = null;
    readyHandledRef.current = false;
    completionHandledAttemptRef.current = null;
    stopOwnedNarration();
    dispatchSession({ type: "replay" });
  }, [dispatchSession, stopOwnedNarration]);

  const handleNextLevel = useCallback(() => {
    const currentSession = sessionRef.current;

    if (
      !isNavigationReady(currentSession) ||
      currentSession.saveResult.nextRecommendation === null
    ) {
      return;
    }

    stopOwnedNarration();
    onPlayLevel(currentSession.saveResult.nextRecommendation.levelId);
  }, [onPlayLevel, stopOwnedNarration]);

  const navigationReady = isNavigationReady(session);
  const resultIsGated =
    session.phase === "result" ||
    session.phase === "saving" ||
    session.phase === "save-error" ||
    navigationReady;
  const ActivityRenderer = renderer.Renderer;

  return (
    <section
      aria-busy={
        session.phase === "result" || session.phase === "saving"
          ? true
          : undefined
      }
      className="play-orchestrator"
      data-activity-type={level.activityType}
      data-domain={level.domain}
      data-level-id={level.id}
      data-reduced-motion={reducedMotion === true}
      data-play-phase={session.phase}
      ref={rootRef}
    >
      <div
        aria-hidden={resultIsGated || undefined}
        className="play-orchestrator__activity"
        inert={resultIsGated ? true : undefined}
      >
        <ActivityRenderer
          caption={narrationCaption ?? null}
          key={session.attemptNumber}
          level={level}
          nowMs={nowMs}
          onCommand={handleCommand}
          onComplete={handleComplete}
          onExit={handleReturnToMap}
          onLevelError={handleLevelError}
          onReady={handleReady}
          reducedMotion={reducedMotion}
          sessionKey={session.attemptNumber}
        />
      </div>

      {session.phase === "result" || session.phase === "saving" ? (
        <section
          aria-labelledby={completionTitleId}
          className="play-orchestrator__result-band"
        >
          <LoaderCircle
            aria-hidden="true"
            className="play-orchestrator__state-icon play-orchestrator__state-icon--saving"
          />
          <h2
            data-play-focus="completion"
            id={completionTitleId}
            tabIndex={-1}
          >
            完成啦
          </h2>
          <p aria-live="polite" role="status">
            点点正在把这次进度收好
          </p>
        </section>
      ) : null}

      {session.phase === "save-error" ? (
        <section
          aria-labelledby={saveErrorTitleId}
          className="play-orchestrator__result-band play-orchestrator__result-band--error"
        >
          <CircleHelp
            aria-hidden="true"
            className="play-orchestrator__state-icon"
          />
          <h2 id={saveErrorTitleId}>进度还没收好</h2>
          <p role="alert">再试一次，点点就能把这次完成记下来。</p>
          <div className="play-orchestrator__actions">
            <PrimaryCommandButton
              data-play-control="retry-save"
              icon={RefreshCw}
              onClick={handleRetrySave}
            >
              再试一次
            </PrimaryCommandButton>
          </div>
        </section>
      ) : null}

      {navigationReady ? (
        <section
          aria-labelledby={readyTitleId}
          className={
            "play-orchestrator__result-band " +
            (session.phase === "saved"
              ? "play-orchestrator__result-band--saved"
              : "play-orchestrator__result-band--temporary")
          }
          data-play-navigation={session.saveResult.navigation}
        >
          <CheckCircle2
            aria-hidden="true"
            className="play-orchestrator__state-icon"
          />
          <h2 data-play-focus="ready" id={readyTitleId} tabIndex={-1}>
            {session.phase === "saved"
              ? "这一站稳稳到达"
              : "这一站到达啦"}
          </h2>
          <p role="status">
            {session.phase === "saved"
              ? "点点已经把这次努力收好啦。"
              : "这一站完成啦。现在是临时模式，关掉游戏后，这次进度不会留下。"}
          </p>
          <div className="play-orchestrator__actions">
            {session.saveResult.nextRecommendation !== null ? (
              <PrimaryCommandButton
                data-play-control="next-level"
                icon={ArrowRight}
                iconPosition="end"
                onClick={handleNextLevel}
              >
                下一站：{session.saveResult.nextRecommendation.title}
              </PrimaryCommandButton>
            ) : null}
            <PrimaryCommandButton
              data-play-control="replay"
              icon={RotateCcw}
              onClick={handleReplay}
            >
              再玩一次
            </PrimaryCommandButton>
            <PrimaryCommandButton
              data-play-control="return-map"
              icon={Map}
              onClick={handleReturnToMap}
            >
              回到岛屿地图
            </PrimaryCommandButton>
          </div>
          {session.phase === "saved" ? (
            <Save
              aria-hidden="true"
              className="play-orchestrator__saved-mark"
            />
          ) : null}
        </section>
      ) : null}
    </section>
  );
}

export function PlayOrchestrator({
  audioCommands,
  levelId,
  levelRegistry,
  narrationCaption,
  nowMs,
  onAttemptExit,
  onAttemptResult,
  onAttemptStart,
  onPlayLevel,
  onReturnToMap,
  profileId,
  reducedMotion,
  rendererRegistry,
  sessionSaver
}: PlayOrchestratorProps) {
  const narrationOwnership = useMemo<NarrationOwnership>(
    () => createNarrationOwnership(),
    []
  );
  const levelResolution = resolvePlayLevel(levelRegistry, levelId);

  if (levelResolution.status === "error") {
    return (
      <PlayRouteError
        code={levelResolution.code}
        onReturnToMap={onReturnToMap}
      />
    );
  }

  if (!profileId.trim()) {
    return (
      <PlayRouteError code="missing-profile" onReturnToMap={onReturnToMap} />
    );
  }

  const rendererResolution = resolvePlayRenderer(
    rendererRegistry,
    levelResolution.level.activityType
  );

  if (rendererResolution.status === "error") {
    return (
      <PlayRouteError
        code={rendererResolution.code}
        onReturnToMap={onReturnToMap}
      />
    );
  }

  return (
    <ResolvedPlaySession
      audioCommands={audioCommands}
      key={
        profileId +
        ":" +
        levelResolution.level.id +
        ":" +
        rendererResolution.registration.activityType
      }
      level={levelResolution.level}
      narrationOwnership={narrationOwnership}
      narrationCaption={narrationCaption}
      nowMs={nowMs}
      onAttemptExit={onAttemptExit}
      onAttemptResult={onAttemptResult}
      onAttemptStart={onAttemptStart}
      onPlayLevel={onPlayLevel}
      onReturnToMap={onReturnToMap}
      profileId={profileId}
      reducedMotion={reducedMotion}
      renderer={rendererResolution.registration}
      sessionSaver={sessionSaver}
    />
  );
}
